import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { NetworkGlyph } from '../../../creatives/NetworkGlyph.js';
import {
  CARD_SX,
  MetricBlock,
  ONE_LINE,
  PotentialPill,
  SpendShareNote,
  VsAvgPill,
} from '../../GemCard.js';
import { CHECKBOX_SX, Pill, PRIMARY_PILL_SX } from '../../Pill.js';
import { formatValue } from '../../resultRows.js';
import type { BriefElement, ElementSelection } from './briefResult.js';

function kpiFormatOf(kpi: string): string {
  return /ctr|rate|share|cvr/i.test(kpi) ? 'percentage' : 'count';
}

// Laid out as the Hidden Gems card, so every workflow shows an element the same way.
export function ElementCard({
  element,
  kpi,
  kpiLabel,
  selectKey,
  selection,
}: {
  element: BriefElement;
  kpi: string;
  kpiLabel?: string | undefined;
  selectKey: string;
  selection: ElementSelection | undefined;
}): ReactNode {
  const best = element.figures.reduce<BriefElement['figures'][number] | null>(
    (top, figure) => (top === null || figure.lift > top.lift ? figure : top),
    null,
  );
  const networks = new Map(
    element.figures.map((figure) => [figure.network, figure.network_label || figure.network]),
  );
  const spend =
    element.spend ?? element.figures.reduce((total, figure) => total + (figure.spend ?? 0), 0);
  const subline = [
    element.context ?? [...networks.values()].join(' · '),
    element.creative_count === undefined
      ? ''
      : `${element.creative_count} creative${element.creative_count === 1 ? '' : 's'}`,
  ]
    .filter((part) => part !== '')
    .join(' · ');

  return (
    <Stack data-testid={`brief-element-${element.feature}-${element.tag}`} sx={CARD_SX}>
      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
        {selection === undefined ? null : (
          <Checkbox
            size="small"
            checked={selection.selectedKeys.includes(selectKey)}
            onChange={() => selection.onToggle(selectKey)}
            sx={{ ...CHECKBOX_SX, mt: 0.25 }}
            slotProps={{ input: { 'aria-label': `Select ${element.feature}: ${element.tag}` } }}
          />
        )}
        <Stack sx={{ gap: 0.5, flex: 1, minWidth: 0 }}>
          <Tooltip title={element.tag}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, ...ONE_LINE }}>
              {element.tag}
            </Typography>
          </Tooltip>
          <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center', minWidth: 0 }}>
            {[...networks.keys()].map((network) => (
              <NetworkGlyph key={network} network={network} />
            ))}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', ...ONE_LINE }}>
              {subline}
            </Typography>
          </Stack>
        </Stack>
        {element.potential === undefined ? (
          <Pill sx={PRIMARY_PILL_SX}>{(element.source_label || element.source).toUpperCase()}</Pill>
        ) : (
          <PotentialPill potential={element.potential} />
        )}
      </Stack>

      {best === null ? (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Not enough evidence for this anchor.
        </Typography>
      ) : (
        <Stack direction="row" sx={{ gap: 5, flexWrap: 'wrap' }}>
          <MetricBlock
            label={kpiLabel || kpi}
            value={formatValue(best.kpi, kpiFormatOf(kpi))}
            note={<VsAvgPill value={best.lift - 1} />}
          />
          {spend > 0 ? (
            <MetricBlock
              label="Spend"
              value={formatValue(spend, 'compact_currency')}
              note={<SpendShareNote share={element.spend_share ?? null} />}
            />
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}
